import { platformDetector } from './PlatformDetector';
import { WebNotificationWrapper } from './WebNotificationWrapper';
import { AndroidNotificationWrapper } from './AndroidNotificationWrapper';
import { IOSNotificationWrapper } from './IOSNotificationWrapper';
export class UnifiedNotificationManager {
    static _instance;
    wrapper = null;
    config;
    isInitialized = false;
    eventListeners = new Map();
    constructor(config = {}) {
        this.config = {
            fallbackEnabled: true,
            ...config
        };
    }
    static getInstance(config) {
        if (!UnifiedNotificationManager._instance) {
            UnifiedNotificationManager._instance = new UnifiedNotificationManager(config);
        }
        return UnifiedNotificationManager._instance;
    }
    async initialize() {
        if (this.isInitialized)
            return true;
        try {
            // Create the appropriate wrapper based on platform
            this.wrapper = this.createWrapper();
            if (!this.wrapper) {
                console.error('[UnifiedNotificationManager] No suitable notification wrapper found');
                return false;
            }
            // Initialize the wrapper
            const success = await this.wrapper.initialize();
            if (!success) {
                console.error('[UnifiedNotificationManager] Wrapper initialization failed');
                return false;
            }
            // Set up global event listeners
            this.setupGlobalEventListeners();
            // Track analytics if available
            if (this.config.analytics) {
                await this.config.analytics.trackEvent('notification_manager_initialized', {
                    platform: this.wrapper.platform,
                    isSupported: this.wrapper.isSupported()
                });
            }
            this.isInitialized = true;
            console.log(`[UnifiedNotificationManager] Initialized with ${this.wrapper.platform} wrapper`);
            return true;
        }
        catch (error) {
            console.error('[UnifiedNotificationManager] Initialization failed:', error);
            return false;
        }
    }
    async destroy() {
        if (!this.isInitialized)
            return;
        try {
            if (this.wrapper) {
                await this.wrapper.destroy();
            }
            // Clean up event listeners
            this.removeGlobalEventListeners();
            this.eventListeners.clear();
            this.wrapper = null;
            this.isInitialized = false;
            console.log('[UnifiedNotificationManager] Destroyed');
        }
        catch (error) {
            console.error('[UnifiedNotificationManager] Error during destroy:', error);
        }
    }
    // Public API methods
    async requestPermission() {
        this.ensureInitialized();
        const result = await this.wrapper.requestPermission();
        // Track analytics
        if (this.config.analytics) {
            await this.config.analytics.trackEvent('notification_permission_requested', {
                platform: this.wrapper.platform,
                granted: result.granted,
                permission: result.permission
            });
        }
        return result;
    }
    getPermissionStatus() {
        this.ensureInitialized();
        return this.wrapper.getPermissionStatus();
    }
    isSupported() {
        return this.wrapper?.isSupported() ?? false;
    }
    async sendImmediate(payload) {
        this.ensureInitialized();
        try {
            await this.wrapper.sendImmediate(payload);
            // Track analytics
            if (this.config.analytics) {
                await this.config.analytics.trackEvent('notification_sent_immediate', {
                    platform: this.wrapper.platform,
                    title: payload.title,
                    type: payload.data?.type || 'unknown'
                });
            }
        }
        catch (error) {
            console.error('[UnifiedNotificationManager] Failed to send immediate notification:', error);
            // Track error
            if (this.config.analytics) {
                await this.config.analytics.trackEvent('notification_send_failed', {
                    platform: this.wrapper.platform,
                    error: error.message,
                    type: 'immediate'
                });
            }
            throw error;
        }
    }
    async schedule(payload) {
        this.ensureInitialized();
        try {
            const id = await this.wrapper.schedule(payload);
            // Track analytics
            if (this.config.analytics) {
                const delay = payload.scheduleTime.getTime() - Date.now();
                await this.config.analytics.trackEvent('notification_scheduled', {
                    platform: this.wrapper.platform,
                    title: payload.title,
                    type: payload.data?.type || 'unknown',
                    delay_ms: delay,
                    challenge_id: payload.challengeId
                });
            }
            return id;
        }
        catch (error) {
            console.error('[UnifiedNotificationManager] Failed to schedule notification:', error);
            // Track error
            if (this.config.analytics) {
                await this.config.analytics.trackEvent('notification_schedule_failed', {
                    platform: this.wrapper.platform,
                    error: error.message,
                    type: 'scheduled'
                });
            }
            throw error;
        }
    }
    async cancel(id) {
        this.ensureInitialized();
        await this.wrapper.cancel(id);
    }
    async cancelAll() {
        this.ensureInitialized();
        await this.wrapper.cancelAll();
        // Track analytics
        if (this.config.analytics) {
            await this.config.analytics.trackEvent('notifications_cancelled_all', {
                platform: this.wrapper.platform
            });
        }
    }
    async cancelByTag(tag) {
        this.ensureInitialized();
        await this.wrapper.cancelByTag(tag);
        // Track analytics
        if (this.config.analytics) {
            await this.config.analytics.trackEvent('notifications_cancelled_by_tag', {
                platform: this.wrapper.platform,
                tag
            });
        }
    }
    async cancelByChallenge(challengeId) {
        // This is a convenience method that uses tags
        await this.cancelByTag(`challenge-${challengeId}`);
    }
    async getPending() {
        this.ensureInitialized();
        return await this.wrapper.getPending();
    }
    async getStats() {
        this.ensureInitialized();
        const pending = await this.getPending();
        const platformInfo = platformDetector.getPlatformInfo();
        return {
            platform: this.wrapper.platform,
            isSupported: this.wrapper.isSupported(),
            permission: this.wrapper.getPermissionStatus(),
            pendingCount: pending.length,
            canUseLocalNotifications: platformInfo.canUseLocalNotifications,
            canUsePushNotifications: platformInfo.canUsePushNotifications
        };
    }
    // Event management
    addEventListener(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event).add(callback);
    }
    removeEventListener(event, callback) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.delete(callback);
            if (listeners.size === 0) {
                this.eventListeners.delete(event);
            }
        }
    }
    // Utility methods
    getPlatform() {
        return this.wrapper?.platform ?? 'web';
    }
    getWrapper() {
        return this.wrapper;
    }
    // Private methods
    createWrapper() {
        const platformInfo = platformDetector.getPlatformInfo();
        const preferredPlatform = this.config.preferredPlatform || platformInfo.platform;
        // Try preferred platform first
        let wrapper = this.createWrapperForPlatform(preferredPlatform);
        if (wrapper?.isSupported()) {
            return wrapper;
        }
        // Fallback logic if enabled
        if (this.config.fallbackEnabled) {
            const fallbackOrder = ['ios', 'android', 'web'];
            for (const platform of fallbackOrder) {
                if (platform === preferredPlatform)
                    continue; // Already tried
                wrapper = this.createWrapperForPlatform(platform);
                if (wrapper?.isSupported()) {
                    console.log(`[UnifiedNotificationManager] Falling back to ${platform} wrapper`);
                    return wrapper;
                }
            }
        }
        return null;
    }
    createWrapperForPlatform(platform) {
        switch (platform) {
            case 'web':
                return new WebNotificationWrapper(this.config.web);
            case 'android':
                return new AndroidNotificationWrapper(this.config.capacitor);
            case 'ios':
                return new IOSNotificationWrapper(this.config.capacitor);
            default:
                return null;
        }
    }
    setupGlobalEventListeners() {
        // Listen for wrapper-specific events and re-emit them
        const notificationClickHandler = (event) => {
            const customEvent = event;
            this.emitEvent('notification-click', customEvent.detail);
        };
        const notificationReceivedHandler = (event) => {
            const customEvent = event;
            this.emitEvent('notification-received', customEvent.detail);
        };
        window.addEventListener('notification-click', notificationClickHandler);
        window.addEventListener('notification-received', notificationReceivedHandler);
    }
    removeGlobalEventListeners() {
        // Remove global event listeners
        // Note: This is simplified - in a real implementation, you'd store references to the actual functions
        window.removeEventListener('notification-click', () => { });
        window.removeEventListener('notification-received', () => { });
    }
    emitEvent(eventName, data) {
        const listeners = this.eventListeners.get(eventName);
        if (listeners) {
            listeners.forEach(callback => {
                try {
                    callback(data);
                }
                catch (error) {
                    console.error(`[UnifiedNotificationManager] Error in event listener for ${eventName}:`, error);
                }
            });
        }
    }
    ensureInitialized() {
        if (!this.isInitialized || !this.wrapper) {
            throw new Error('UnifiedNotificationManager must be initialized before use');
        }
    }
}
// Export singleton instance
export const notificationManager = UnifiedNotificationManager.getInstance();
